import { useEffect, useMemo, useState } from "react";
import { useQueries } from "react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Plug, Search, Settings2 } from "lucide-react";
import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import { useToast } from "../../../hooks/use-toast";
import EmptyStateCard from "../EmptyStateCard";
import { saasApi } from "./shared/saasApi";
import { SaasConnection, SaasProvider } from "./shared/saasTypes";
import { connectionHealth, ConnectionTone, formatNumber, PROVIDERS } from "./shared/saasFormat";
import { MetaTag, StatusPill, ToolbarSearch, useSaasTenant } from "./shared/SaasUi";
import SaasWorkspace from "./workspace/SaasWorkspace";

const SUPPORTED: SaasProvider[] = ["github", "gitlab"];
const DESCRIPTIONS: Record<SaasProvider, string> = {
    github: "Organization members, teams, repository access, branch protection and audit activity",
    gitlab: "Group and project members, access levels, protected branches and audit activity",
};
const TONE_RANK: Record<ConnectionTone, number> = { healthy: 0, working: 1, warning: 2, error: 3 };

const isProvider = (value: string | null): value is SaasProvider => value === "github" || value === "gitlab";

export default function ConfiguredSaasAppsDetails() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { toast } = useToast();
    const tenantName = useSaasTenant();
    const [searchTerm, setSearchTerm] = useState("");
    const requested = searchParams.get("provider");
    const [activeApp, setActiveApp] = useState<SaasProvider | null>(isProvider(requested) ? requested : null);

    useEffect(() => {
        const status = searchParams.get("status");
        if (!status) return;
        const message = searchParams.get("message") ?? "";
        if (status === "CONNECTED") {
            toast({ title: "Connected", description: message });
        } else if (status === "PENDING_APPROVAL") {
            toast({ title: "Waiting for approval", description: message });
        } else {
            toast({ title: "Connection failed", description: message, variant: "destructive" });
        }
        setSearchParams(
            (previous) => {
                const next = new URLSearchParams(previous);
                ["status", "message", "account"].forEach((key) => next.delete(key));
                return next;
            },
            { replace: true },
        );
    }, [searchParams, setSearchParams, toast]);

    const connectionQueries = useQueries(
        SUPPORTED.map((provider) => ({
            queryKey: ["saas-connections", provider, tenantName],
            queryFn: () => saasApi.connections(provider, tenantName),
            enabled: Boolean(tenantName),
        })),
    );

    const apps = useMemo(
        () =>
            SUPPORTED.map((provider, index) => ({
                provider,
                connections: (connectionQueries[index]?.data as SaasConnection[] | undefined) ?? [],
                loading: Boolean(connectionQueries[index]?.isLoading),
            })),
        [connectionQueries],
    );

    const loading = !tenantName || apps.some((app) => app.loading);
    const configured = apps.filter((app) => app.connections.length > 0);
    const visible = configured.filter(({ provider }) => {
        const query = searchTerm.trim().toLowerCase();
        return !query || PROVIDERS[provider].label.toLowerCase().includes(query) || DESCRIPTIONS[provider].toLowerCase().includes(query);
    });

    const openApp = (provider: SaasProvider | null) => {
        setActiveApp(provider);
        setSearchParams(provider ? { provider } : {}, { replace: true });
    };

    if (activeApp) {
        return <SaasWorkspace provider={activeApp} onBack={() => openApp(null)} />;
    }

    return (
        <div className="flex-1 space-y-6 p-6">
            <div className="resource-request-toolbar">
                <ToolbarSearch value={searchTerm} onChange={setSearchTerm} placeholder="Search connected apps..." />
                <Button variant="outline" className="saas-outline-button ml-auto" onClick={() => navigate("/tenantSettings?setting=saas-apps")}>
                    <Settings2 className="w-4 h-4" />
                    Manage SaaS connections
                </Button>
            </div>

            {loading ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {[1, 2].map((index) => (
                        <Card key={index} className="cloud-card-surface rounded-2xl h-48 animate-pulse" />
                    ))}
                </div>
            ) : configured.length === 0 ? (
                <EmptyStateCard
                    icon={Plug}
                    title="No SaaS apps connected"
                    description="Connect GitHub or GitLab from Settings to monitor identities, access and audit activity."
                    action={<Button onClick={() => navigate("/tenantSettings?setting=saas-apps")}>Connect an app</Button>}
                    className="cloud-card-surface border-dashed rounded-2xl"
                />
            ) : visible.length === 0 ? (
                <EmptyStateCard icon={Search} title="No apps found" description="Try adjusting your search terms."
                                className="cloud-card-surface border-dashed rounded-2xl" />
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {visible.map(({ provider, connections }) => {
                        const meta = PROVIDERS[provider];
                        const Icon = meta.icon;
                        const health = connections.map(connectionHealth).sort((left, right) => TONE_RANK[right.tone] - TONE_RANK[left.tone])[0];
                        const identities = connections.reduce((sum, connection) => sum + Number(connection.details?.identityCount ?? 0), 0);
                        const resources = connections.reduce((sum, connection) => sum + Number(connection.details?.resourceCount ?? 0), 0);
                        return (
                            <Card
                                key={provider}
                                className="cloud-list-card cursor-pointer rounded-2xl"
                                onClick={() => openApp(provider)}
                                data-testid={`card-configured-saas-${provider}`}
                            >
                                <CardContent className="p-6">
                                    <div className="flex items-start justify-between mb-4 gap-3">
                                        <div className="w-14 h-14 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 rounded-xl flex items-center justify-center border border-blue-200 dark:border-blue-700">
                                            <Icon className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                                        </div>
                                        {health && <StatusPill tone={health.tone} label={health.label} title={health.detail} />}
                                    </div>
                                    <div className="space-y-3">
                                        <h3 className="font-semibold text-lg leading-tight">{meta.label}</h3>
                                        <p className="text-sm text-muted-foreground leading-relaxed">{DESCRIPTIONS[provider]}</p>
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <MetaTag>{`${connections.length} account${connections.length === 1 ? "" : "s"}`}</MetaTag>
                                            <MetaTag>{`${formatNumber(identities)} identities`}</MetaTag>
                                            <MetaTag>{`${formatNumber(resources)} resources`}</MetaTag>
                                        </div>
                                    </div>
                                    <div className="mt-4 pt-4 border-t flex items-center justify-between text-sm">
                                        <span className="text-muted-foreground truncate">
                                            {connections.map((connection) => connection.accountLogin ?? connection.accountId).join(", ")}
                                        </span>
                                        <span className="flex items-center gap-1 text-primary font-medium flex-shrink-0">
                                            Open
                                            <ArrowRight className="w-4 h-4" />
                                        </span>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
